import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApipaymentVerifyCall, getApiCall } from '../../utils/Apicalls';
import Header from "../../components/common/header/Header";
import Footer from "../../components/common/footer/Footer";
import SpinnerShow from '../../components/Spinner';
import { CheckCircle } from '@mui/icons-material';
import CurrencyRupee from '@mui/icons-material/CurrencyRupee';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import CurrencyPound from '@mui/icons-material/CurrencyPound';
import CurrencyYen from '@mui/icons-material/CurrencyYen';
import AttachMoney from '@mui/icons-material/AttachMoney';
import './PaymentResult.css';

function PaymentSuccessPage() {
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // Function to get currency symbol based on country code
  const getCurrencySymbol = (countryCode) => {
    // Map of country codes to currency symbols
    const currencyMap = {
      'IN': { symbol: <CurrencyRupee fontSize="small" />, code: 'INR' },
      'GB': { symbol: <CurrencyPound fontSize="small" />, code: 'GBP' },
      'UK': { symbol: <CurrencyPound fontSize="small" />, code: 'GBP' },
      'EU': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'DE': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'FR': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'IT': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'ES': { symbol: <EuroSymbol fontSize="small" />, code: 'EUR' },
      'JP': { symbol: <CurrencyYen fontSize="small" />, code: 'JPY' },
      // Default to USD for all other countries
      'US': { symbol: <AttachMoney fontSize="small" />, code: 'USD' },
    };
    
    return currencyMap[countryCode] || currencyMap['US'];
  };
  
  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get purchase ID from localStorage
        const purchaseId = localStorage.getItem('currentPurchaseId');
        const email = localStorage.getItem('email');
        
        if (!purchaseId) {
          setError("Invalid payment session");
          setLoading(false);
          return;
        }

        // Build verification URL
        const buildVerificationUrl = () => {
          const baseUrl = `interview-app/purchaseplan/complete/${purchaseId}`;
          return email ? `${baseUrl}?email=${email}` : baseUrl;
        };

        // Call verification API
        const verificationUrl = buildVerificationUrl();
        const result = email 
          ? await getApipaymentVerifyCall(verificationUrl, email)
          : await getApiCall(verificationUrl);
        
        console.log("Payment verification result:", result);

        // Validate API response
        if (!result?.status || !result?.result) {
          setError(result?.message || "Failed to verify payment");
          setLoading(false);
          return;
        }

        // Check payment completion status
        const { payment_status, status, country, amount_total, purchase_minutes } = result.result;
        
        if (payment_status !== 'paid' || status !== 'complete') {
          setError("Payment was not completed successfully");
          setLoading(false);
          return;
        }

        // Extract payment details
        const currencyInfo = getCurrencySymbol(country || 'US');
        
        setPaymentDetails({
          planName: localStorage.getItem('currentPlanName') || 'Interview Plan',
          amount: (amount_total / 100).toFixed(2),
          timeAdded: purchase_minutes || '0',
          transactionId: purchaseId,
          country: country || 'Unknown',
          currencySymbol: currencyInfo.symbol,
          currencyCode: currencyInfo.code
        });

        // Clear purchase data from localStorage
        localStorage.removeItem('currentPurchaseId');
        localStorage.removeItem('currentPlanName');
        localStorage.removeItem('currentPlanPrice');
        localStorage.removeItem('currentPlanTime');
        localStorage.removeItem('email');

      } catch (err) {
        console.error("Error verifying payment:", err);
        setError("An error occurred while verifying your payment");
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, []);
  
  const handleGoToHome = () => {
    navigate('/');
  };
  
  const handleGoToInterview = () => {
    navigate('/');
  };
  
  if (loading) {
    return (
      <>
        <Header />
        <div className="payment-result-container">
          <div className="payment-result-card loading">
            <SpinnerShow />
            <p>Verifying your payment...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <Header />
        <div className="payment-result-container">
          <div className="payment-result-card error">
            <h2>Payment Verification Failed</h2>
            <p>{error}</p>
            <button className="payment-result-button" onClick={handleGoToHome}>
              Return to Home
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  return (
    <>
      <Header />
      <div className="payment-result-container">
        <div className="payment-result-card success">
          <div className="success-icon">
            <CheckCircle />
          </div>
          <h1>Payment Successful!</h1>
          <p className="success-message">
            Thank you for your purchase. Your interview time has been added to your account.
          </p>
          
          {paymentDetails && (
            <div className="payment-details">
              <div className="payment-detail-item">
                <span>Plan:</span>
                <span>{paymentDetails.planName}</span>
              </div>
              <div className="payment-detail-item">
                <span>Amount Paid:</span>
                <span className="currency-amount">
                  {paymentDetails.currencySymbol}
                  {paymentDetails.amount}
                  {paymentDetails.currencyCode !== 'USD' && ` ${paymentDetails.currencyCode}`}
                </span>
              </div>
              <div className="payment-detail-item">
                <span>Time Added:</span>
                <span>{paymentDetails.timeAdded} minutes</span>
              </div>
              <div className="payment-detail-item">
                <span>Transaction ID:</span>
                <span>{paymentDetails.transactionId}</span>
              </div>
              {paymentDetails.country && (
                <div className="payment-detail-item">
                  <span>Billing Country:</span>
                  <span>{paymentDetails.country}</span>
                </div>
              )}
            </div>
          )}
          
          <div className="payment-result-actions">
            <button 
              className="payment-result-button primary" 
              onClick={handleGoToInterview}
            >
              Start Interviewing Now
            </button>
            <button 
              className="payment-result-button secondary" 
              onClick={handleGoToHome}
            >
              Return to Home
            </button>
          </div>
          
          <div className="payment-receipt-info">
            <p>A receipt has been sent to your email address.</p>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default PaymentSuccessPage;




